import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { AuthService } from "./auth.service";
import { DatePipe } from "@angular/common";
import { TranslateService } from "@ngx-translate/core";
import { UsersSearchParams, UiUser, UserRole } from "../models/ui-user.model";
import { Observable } from "rxjs";
import { ServiceOptions } from "../models/service-options";
import { map, catchError, take } from "rxjs/operators";
import { ServiceMetaData } from "../models/service-meta.model";
import { DataElement, Doctor, ServiceCallInfo } from "../models/common";
import { PhoneAreaCodePipe } from "../components/ui/pipes/phone-area-code.pipe";
import { FormatPhonePipe } from "../components/ui/pipes/format-phone.pipe";
import { Clinic } from "../models/clinic.model";
import { Store } from "@ngrx/store";
import { GET_TASK_USERS, GET_USERS } from "../ngrx/ngrx-actions";

@Injectable({
  providedIn: "root",
})
export class UsersService {
  constructor(
    private readonly store: Store,
    private http: HttpClient,
    private authService: AuthService,
    private datePipe: DatePipe,
    private translateService: TranslateService,
    private phoneAreaCodePipe: PhoneAreaCodePipe,
    private formatPhonePipe: FormatPhonePipe
  ) {}

  getUsers(
    usersSearchParams: UsersSearchParams,
    dispatchUsers: boolean = true,
    activeOnly: boolean = false,
    dispatchLabUsers: boolean = false
  ): Observable<UiUser[]> {
    let response = new Observable<UiUser[]>();
    let serviceName = "get_users";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    if (usersSearchParams.username)
      params["username"] = usersSearchParams.username;

    if (usersSearchParams.role) params["roleCode"] = usersSearchParams.role.id;
    if (usersSearchParams.org) params["org"] = usersSearchParams.org;

    if (usersSearchParams.clinic)
      params["clinicId"] = usersSearchParams.clinic.id;

    if (usersSearchParams.plant) params["plantId"] = usersSearchParams.plant.id;

    if (activeOnly) params["activeSw"] = 1;

    params["taskUsersSw"] = usersSearchParams.taskUsersSw ? 1 : 0;
    params["generalTasksSw"] = usersSearchParams.generalTasksSw ? 1 : 0;
    params["fromUserEditSw"] = usersSearchParams.fromUserEditSw;

    if (usersSearchParams.rangeFrom || usersSearchParams.rangeFrom === 0)
      params["fromRec"] = usersSearchParams.rangeFrom;
    if (usersSearchParams.rangeTo) params["toRec"] = usersSearchParams.rangeTo;
    if (usersSearchParams.searchId || usersSearchParams.searchId === 0)
      params["searchId"] = usersSearchParams.searchId;
    params.urldecodeSw = 1;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) =>
          this.extractGetUsers(
            data,
            usersSearchParams,
            dispatchUsers,
            dispatchLabUsers,
            info
          )
        ),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractGetUsers(
    res: any,
    params: UsersSearchParams,
    dispatchUsers: boolean,
    dispatchTaskUsers: boolean,
    info: ServiceCallInfo
  ): UiUser[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: UiUser[] = [];
    let data = res.reply.requestResult.data;
    let metaData = new ServiceMetaData();

    if (data && data.user) {
      let usersTag = data.user;
      let metaTag = data.metadata;
      metaData.totalRecords = metaTag.totRec;
      metaData.fromRecord = metaTag.fromRec;
      metaData.toRecord = metaTag.toRec;
      metaData.searchId = metaTag.searchId;

      if (usersTag) {
        let arr = new Array().concat(usersTag);
        arr.forEach((i) => {
          result.push(this._extractUserTag(i));
        });
      }
    }

    result.loading = false;

    if (dispatchUsers)
      this.store.dispatch(
        GET_USERS({
          data: result,
          medataData: metaData,
        })
      );

    if (dispatchTaskUsers)
      this.store.dispatch(
        GET_TASK_USERS({
          data: result,
          medataData: metaData,
        })
      );
    return result;
  }

  _extractUserTag(i: any): UiUser {
    let user = new UiUser();
    user.username = i.username;
    user.roleCode = i.roleCode;
    user.roleDescr = i.roleDescr;
    user.role = new UserRole(user.roleCode, user.roleDescr, i.org);
    if (i.clinicId) {
      user.clinic = new Clinic(i.clinicId, i.clinicDescr);
      user.clinicDescr = user.clinic.text;
    }
    if (i.plantId) {
      user.plant = new DataElement(i.plantId, i.plantDescr);
    }
    if (i.doctorId) {
      user.doctor = new Doctor(i.doctorId, i.doctorDescr);
    }
    user.phone = this.formatPhonePipe.transform(i.phone);
    user.password = i.password;
    user.userId = i.userId;
    user.activeSw = this.authService.fromXMLBoolean(i.activeSw);
    user.cognitoStatusSw = this.authService.fromXMLBoolean(i.cognitoActiveSw);
    user.userDescr = i.userDescr;
    user.lastSeen = i.lastSeen;
    return user;
  }

  updateUser(user: UiUser): Observable<UiUser> {
    let response = new Observable<UiUser>();
    let serviceName = "update_user";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["updUserId"] = user.userId;
    params["username"] = user.username.toUpperCase();
    params["password"] = user.password;
    params["roleCode"] = user.role.id;
    if (user.clinic) params["clinicId"] = user.clinic.id;
    if (user.plant) params["plantId"] = user.plant.id;
    if (user.doctor) params["doctorId"] = user.doctor.id;
    params["phone"] = this.phoneAreaCodePipe.transform(user.phone);
    params["userDescr"] = user.userDescr;
    if (user.activeSw != null) {
      params["activeSw"] = user.activeSw ? 1 : 0;
    }

    params.urldecodeSw = 1;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractUpdateUser(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractUpdateUser(res: any, info: ServiceCallInfo): UiUser {
    this.authService.extractRequestStatusNew(info, res);
    let result: UiUser = new UiUser();
    let data = res.reply.requestResult.data;

    if (data && data.user) {
      result = this._extractUserTag(data.user);
    }

    return result;
  }

  public syncUsers(user: UiUser): Observable<void> {
    let response = new Observable<void>();
    let serviceName = "SyncCognitoUsers.do";
    let params = {};
    params["originApp"] = "brosh_on_line";
    params["serviceName"] = serviceName;
    params["urldecodeSw"] = 0;
    params["username"] = this.authService.currentUser.username;
    params["data"] = {};
    params["data"]["username"] = user.username;

    let req = {};
    req["request"] = params;

    let url = this.authService.getRequestUrl(serviceName, true);
    let body = JSON.stringify(req);
    let info = new ServiceCallInfo("SyncCognitoUsers.do", url, body, true);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          this.authService.extractRequestStatusNew(info, data);
          console.log(data);
        }),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }
}
